import {
  Document,
  I18nLabel,
  Issue,
  IssueDownloadsResponse,
  PollDayDownloadsResponse,
  PollOverview,
} from 'api/api.types';
import { useTranslation } from 'react-i18next';
import { getFileIcon } from '@ktag/styleguide/dist/util';
import { useContext } from 'react';
import ApiContext, { DownloadsType, isArchiveId } from 'api/ApiContext';
import { UseQueryResult } from 'react-query';
import getIssueType, { ISSUE_FILTER_TYPES } from 'utils/getIssueType';
import { getSortkeyForLocalIssue } from 'api/sortLocalElections';

interface DocumentWithLabel extends Document {
  label: I18nLabel;
}

interface DocumentGroup {
  group: string;
  sortKey: number;
  downloads: DocumentWithLabel[];
  info?: PollOverview;
}

const useTransformDownloads = (
  type: DownloadsType,
  query: UseQueryResult<PollDayDownloadsResponse | IssueDownloadsResponse, unknown>,
  issues?: Issue[],
  issueId?: string,
) => {
  const { language, apiUrl, archiveDownloadsUrl } = useContext(ApiContext);
  const { t } = useTranslation('wap');

  const formatDocument = (document: Document) => {
    const baseUrl = isArchiveId(issueId) ? archiveDownloadsUrl : apiUrl;
    return {
      linkText: document.label?.[language],
      sort_key: document.sort_key,
      icon: { id: getFileIcon({ extension: document.type }) },
      target: {
        url: `${baseUrl}${document.path[language]}`,
        external: true,
        type: 'download',
        accessible: true,
      },
      meta: document.type.toUpperCase(),
    };
  };

  // generate downloads list for details page
  const issueDownloads = type === 'issues' && (query.data as IssueDownloadsResponse);

  const formattedIssueDownloads = issueDownloads?.data?.documents?.map(formatDocument);

  const sortedIssueDownloads = formattedIssueDownloads?.sort((a, b) => {
    if (a.sort_key < b.sort_key) {
      return -1;
    }
    if (a.sort_key > b.sort_key) {
      return 1;
    }
    return 0;
  });

  // generate grouped downloads list for poll day page
  const pollingDayDownloads =
    type === 'polling_days' && (query.data as PollDayDownloadsResponse);

  // Give each issue type a sort key. This will be used to sort the groups among each other. (Show national council downloads before local elections downloads, ..)
  // Within these groups, we later use the file's sort key to sort the files.
  const issueTypes = issues?.map((issue) => {
    const issueType = getIssueType(issue.info);
    const getIssueGroup = () => {
      switch (issueType) {
        case ISSUE_FILTER_TYPES.poll:
          return { label: t('polls'), sortKey: 0 };
        case ISSUE_FILTER_TYPES.nationalCouncil:
          return { label: t('national_council_election'), sortKey: 1 };
        case ISSUE_FILTER_TYPES.grossRat:
          return { label: t('grossrat_election'), sortKey: 2 };
        case ISSUE_FILTER_TYPES.governmentCouncil:
          if (issue.info.title.de.toLowerCase().includes('regierungsrat')) {
            return { label: t('government_council_election'), sortKey: 4 };
          }
          return { label: t('council_of_states_election'), sortKey: 3 };
        case ISSUE_FILTER_TYPES.localElection:
          return { label: t('downloads_local_election'), sortKey: 5 };
        default:
          return { label: t('other'), sortKey: 6 };
      }
    };

    return {
      id: issue.info.id,
      group: getIssueGroup().label,
      sortKey: getIssueGroup().sortKey,
      info: issue.info,
    };
  });

  const formattedPollingDaysDownloads: DocumentGroup[] = pollingDayDownloads?.data?.map(
    (fileGroup) => {
      const issue = issueTypes.find(
        (issueType) => fileGroup.associated_issue_ids[0] === issueType.id,
      );

      return {
        group: issue.group,
        sortKey: issue.sortKey,
        info: issue.info,
        downloads: fileGroup.documents.map((document) => {
          return {
            ...document,
            label: {
              de: document.label.de,
            },
          };
        }),
      };
    },
  );
  const pollingDayDownloadGroups: { label: string; sortKey: number; downloads: any[] }[] =
    [];

  // sort each file within its group by sort key
  formattedPollingDaysDownloads?.forEach((group) => {
    group.downloads.sort((a, b) => {
      if (a.sort_key < b.sort_key) {
        return -1;
      }
      if (a.sort_key > b.sort_key) {
        return 1;
      }
      return 0;
    });
  });

  // look into every document group, find the lowest sort key of any document within and sort the groups by that
  // this way, documents belonging to one group (e.g. Election Aargau) will stay together
  // yet all downloads remain a logical order that fits that of the issue details page
  formattedPollingDaysDownloads?.sort((a, b) => {
    if (a.downloads[0].sort_key < b.downloads[0].sort_key) {
      return -1;
    }
    if (a.downloads[0].sort_key > b.downloads[0].sort_key) {
      return 1;
    }
    return 0;
  });

  // merge those document arrays that belong to the same group (e.g. all national council downloads)
  formattedPollingDaysDownloads?.forEach((entry) => {
    if (pollingDayDownloadGroups.some((group) => group.label === entry.group)) {
      pollingDayDownloadGroups
        .find((group) => group.label === entry.group)
        .downloads.push(
          ...entry.downloads.map(formatDocument).map((download) => ({
            ...download,
            issueInfo: entry.info,
          })),
        );
    } else {
      pollingDayDownloadGroups.push({
        label: entry.group,
        sortKey: entry.sortKey,
        downloads: entry.downloads.map(formatDocument).map((download) => ({
          ...download,
          issueInfo: entry.info,
        })),
      });
    }
  });

  const groupsWithoutDuplicateDownloads = pollingDayDownloadGroups.map(
    (downloadGroup) => ({
      ...downloadGroup,
      downloads: downloadGroup.downloads.filter(
        (download, index, self) =>
          index === self.findIndex((d) => d.target.url === download.target.url),
      ),
    }),
  );

  const localDownloadGroup = groupsWithoutDuplicateDownloads?.find(
    (group) => group.label === t('downloads_local_election'),
  );

  if (localDownloadGroup) {
    localDownloadGroup.downloads.sort((a, b) => {
      if (getSortkeyForLocalIssue(a.issueInfo) > getSortkeyForLocalIssue(b.issueInfo)) {
        return 1;
      }
      if (getSortkeyForLocalIssue(a.issueInfo) < getSortkeyForLocalIssue(b.issueInfo)) {
        return -1;
      }
      return 0;
    });
  }

  const sortedGroupsWithoutDuplicateDownloads = groupsWithoutDuplicateDownloads.map(
    (group) => {
      return {
        ...group,
        downloads: group.downloads.sort((a, b) => a.sort_key - b.sort_key),
      };
    },
  );

  return {
    formattedIssueDownloads: sortedIssueDownloads,
    downloadsGroups: sortedGroupsWithoutDuplicateDownloads,
  };
};

export default useTransformDownloads;
